/**
 * forecastHero.ts — Forecast programme F2 (register row 70): the smart
 * forecast hero's MODEL, pure, from ONE /v1/sales/forecast response. The
 * component (ForecastHero.tsx) renders exactly what this returns.
 *
 * Spec: docs/design/calm-minimal/specs/klaxiora-forecast-spec.md §3.2, under
 * the rulings of docs/design/stier/ADOPTION.md §18 and the 2026-09-29 F2
 * rulings (build log §38.10):
 *
 * - EVERY FIGURE IS THE WIRE'S, with the wire's own fence count beside it —
 *   Locked, Stretch, commitDue, bestCaseStretch, the pull-ins, the overdue
 *   list and the next period are rendered, never re-derived from deals. The
 *   one sum made here is Won (period) = the two attribution halves, pence and
 *   counts, which is exactly how the server builds Locked from them.
 * - THE CLOCK IS THE SERVER'S: elapsed %, day-of-period, days left and past /
 *   future all read `today` from the wire. The client clock is never read.
 * - NO LIVE DOT (§18.7): the as-of time of the fetch (`asOf`) replaces it.
 * - TARGETS (§18.7): the team target is Σ rep targets, `totalTargetPence`;
 *   where the spec says "No {period} target" the copy says what is true — no
 *   rep has a target set. Won against that target is ATTRIBUTED won (RULED
 *   2026-09-29): only won value linked to an employee can attain a rep
 *   target, the Attribution panel's own rule.
 * - INSIGHT RULE 1 IS WITHHELD (RULED 2026-09-29): "no open deals dated
 *   inside the rest of the period" needs a count of ALL open deals due in
 *   [today, end), and the wire carries only the Commit and Best case slices.
 *   It never fires; the Locked sentence uses the form the wire proves. Its
 *   input is its own register row (an openDue triple on /forecast).
 * - A pre-F1 response (no `locked` / `today`) yields null: the hero renders
 *   nothing rather than zeros.
 */
import type { SalesFencedFigure, SalesForecast, SalesForecastDealRef } from '../../../../api/types'
import { money } from '../../../../lib/money'
import { SLUG_TO_STAGE } from '../../api'
import { fmtMoney } from './SalesDashboard'
import { fencedDealCount } from './forecastView'
import { forecastCategoryLabel } from './vocab'

export type Scenario = 'locked' | 'stretch'
export type PeriodState = 'future' | 'current' | 'ended'

const DAY_MS = 86_400_000
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const MONTH_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/** Insight rule 1's input is not on the wire (see the file header). */
export const RULE_1_EVALUABLE = false
/** The footer strip's "Next up" form shows at this many days left or fewer. */
export const NEXT_UP_DAYS = 14
/** Rule 4: behind pace when won ÷ target trails elapsed by more than this. */
export const PACE_GAP_PTS = 10

const utc = (iso: string) => {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
  return Date.UTC(y, m - 1, d)
}
const dayMonth = (ms: number) => { const d = new Date(ms); return `${d.getUTCDate()} ${MONTH_ABBR[d.getUTCMonth()]}` }
const weekdayDayMonth = (ms: number) => `${WEEKDAY[new Date(ms).getUTCDay()]} ${dayMonth(ms)}`

/** The period's name from its own start — "Q3 2026" / "Sep 2026". */
export function periodName(kind: string, startIso: string): string {
  const d = new Date(utc(startIso))
  if (kind === 'quarter') return `Q${Math.floor(d.getUTCMonth() / 3) + 1} ${d.getUTCFullYear()}`
  return `${MONTH_ABBR[d.getUTCMonth()]} ${d.getUTCFullYear()}`
}

/** §3.8: headline values in the short format, two decimals under £10M
 *  (£7.02M); the exact value rides the element's title. Below £1M and at
 *  £10M or more, the house compactor (fmtMoney). */
export function headlineMoney(pence: number): string {
  const n = pence / 100
  if (n >= 1_000_000 && n < 10_000_000) return `£${(n / 1_000_000).toFixed(2)}M`
  return fmtMoney(pence)
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many)

/** A figure's fence note — "" when nothing was dropped. */
export function fenceNote(f: SalesFencedFigure): string {
  const n = f.excludedNonGbp
  if (!n) return ''
  return `${n} non-GBP ${plural(n, 'deal has', 'deals have')} no captured rate and ${plural(n, 'is', 'are')} not in this figure.`
}

export interface PeriodClock {
  state: PeriodState
  daysInPeriod: number
  /** 1-based day of the period ON the server's today; 0 before, daysInPeriod after. */
  dayOfPeriod: number
  /** Days after today up to and including the last day (28 Sep in Q3 → 2). */
  daysLeft: number
  elapsedPct: number
  lastDayMs: number
  nextStartMs: number
}

/** The server's date against the period — §18.9's reading: 28 Sep in Q3 2026
 *  is "Day 90 of 92", 98% elapsed, 2 days left. */
export function periodClock(periodStart: string, periodEnd: string, today: string): PeriodClock {
  const s = utc(periodStart), e = utc(periodEnd), t = utc(today)
  const daysInPeriod = Math.round((e - s) / DAY_MS)
  let state: PeriodState = 'current'
  let dayOfPeriod = Math.round((t - s) / DAY_MS) + 1
  if (t < s) { state = 'future'; dayOfPeriod = 0 }
  if (t >= e) { state = 'ended'; dayOfPeriod = daysInPeriod }
  const daysLeft = state === 'current' ? Math.round((e - t) / DAY_MS) - 1 : 0
  return {
    state, daysInPeriod, dayOfPeriod, daysLeft,
    elapsedPct: Math.round((dayOfPeriod / daysInPeriod) * 100),
    lastDayMs: e - DAY_MS, nextStartMs: e,
  }
}

export interface Insight {
  rule: 2 | 3 | 4 | 5 | 6
  tone: 'info' | 'warning' | 'danger' | 'success'
  title: string
  body: string
  /** A deal the link opens — present only when there is ONE deal to open. */
  link?: { label: string; dealId: string }
}

export interface HeroModel {
  period: string
  clock: PeriodClock
  asOfLabel: string
  scenario: Scenario
  /** False for an ended period: its Stretch IS its Locked (the server
   *  collapses the horizon), so there is nothing to switch between. */
  showSwitch: boolean
  label: string
  headline: SalesFencedFigure
  headlineText: string
  headlineExact: string
  sentence: string
  fenceNote: string
  won: SalesFencedFigure
  bar: { wonPct: number; stretchPct: number; left: string; right: string }
  insights: Insight[]
  rings: {
    outerPct: number | null
    outerLabel: string
    elapsedPct: number
    elapsedLabel: string
    caption: string
    ariaLabel: string
  }
  footer: { kind: 'next' | 'pace'; text: string } | null
}

const pctOf = (num: number, den: number) => (den > 0 ? Math.round((num / den) * 100) : null)
const clampPct = (p: number | null) => (p === null ? 0 : Math.max(0, Math.min(100, p)))

function dealWords(d: SalesForecastDealRef): string {
  const stage = SLUG_TO_STAGE[d.stage] ?? d.stage
  const date = d.expectedClose ? dayMonth(utc(d.expectedClose)) : 'no date'
  return `"${d.title}" is ${forecastCategoryLabel(d.category)} in ${stage} but dated ${date}.`
}

function namesList(items: SalesForecastDealRef[], max = 3): string {
  const names = items.slice(0, max).map(d => `"${d.title}"`)
  const more = items.length - names.length
  return more > 0 ? `${names.join(', ')} and ${more} more` : names.join(', ')
}

/** The model, or null for a response that predates F1. */
export function heroModel(f: SalesForecast, scenarioAsked: Scenario): HeroModel | null {
  if (!f.locked || !f.stretch || !f.commitDue || !f.bestCaseStretch || !f.today || !f.asOf) return null
  const clock = periodClock(f.periodStart, f.periodEnd, f.today)
  const period = periodName(f.periodKind, f.periodStart)
  const kindWord = f.periodKind === 'quarter' ? 'quarter' : 'month'
  const end = dayMonth(clock.lastDayMs)
  const ended = clock.state === 'ended'
  const scenario: Scenario = ended ? 'locked' : scenarioAsked
  const headline = scenario === 'stretch' ? f.stretch : f.locked

  const won: SalesFencedFigure = {
    pence: f.attributedWonPence + f.unattributedWonPence,
    count: f.attributedWonCount + f.unattributedWonCount,
    excludedNonGbp: (f.attributedWonExcludedNonGbp ?? 0) + (f.unattributedWonExcludedNonGbp ?? 0),
  }
  const wonText = money(won.pence / 100)
  const commit = f.commitDue, best = f.bestCaseStretch
  const pullIns = f.pullIns ?? []

  // ── the sentence under the headline ──
  let sentence: string
  if (ended) {
    sentence = `Won ${wonText} across ${fencedDealCount(won.count, won.excludedNonGbp)} in ${period}.`
  } else if (scenario === 'locked') {
    // Rule 1's form ("No other open deal is dated before …") is withheld —
    // the wire cannot prove it. This form states only what the wire carries.
    sentence = commit.count > 0
      ? `Won ${wonText} so far; ${fencedDealCount(commit.count, commit.excludedNonGbp)} in Commit worth ${money(commit.pence / 100)} ${plural(commit.count, 'is', 'are')} still due by ${end}.`
      : `Won ${wonText} so far; no Commit deal is still due by ${end}.`
  } else if (best.count === 0) {
    sentence = `No open Best case deal is dated before ${dayMonth(clock.lastDayMs + 31 * DAY_MS)}, so Stretch is the Locked figure.`
  } else {
    const bestPullIns = pullIns.filter(d => d.category === 'best_case')
    const one = best.count === 1 && bestPullIns.length === 1 && bestPullIns[0].pence === best.pence ? bestPullIns[0] : null
    const lead = one
      ? `If "${one.title}" (${money(best.pence / 100)}, Best case) closes by ${end} instead of ${dayMonth(utc(one.expectedClose!))}.`
      : `If ${best.count} Best case ${plural(best.count, 'deal', 'deals')} worth ${money(best.pence / 100)} close by ${end}.`
    const rest = commit.count === 0 && commit.excludedNonGbp === 0 ? 'Everything else is already won.' : 'The rest is won or in Commit.'
    sentence = `${lead} ${rest}`
  }

  // ── the bar ──
  const denom = headline.pence
  const wonPct = clampPct(pctOf(won.pence, denom))
  const stretchPct = scenario === 'stretch' ? clampPct(pctOf(best.pence, denom)) : 0
  const bar = {
    wonPct, stretchPct,
    left: `Won ${fmtMoney(won.pence)} · ${fencedDealCount(won.count, won.excludedNonGbp)}`,
    right: scenario === 'stretch'
      ? `Stretch +${fmtMoney(best.pence)}`
      : commit.count > 0 ? `Commit due +${fmtMoney(commit.pence)}` : '',
  }

  // ── "What matters now": rules 2–6 in priority order, first three ──
  const target = f.totalTargetPence
  const attributed = f.attributedWonPence
  const insights: Insight[] = []
  if (!ended && pullIns.length > 0) {
    const d = pullIns[0]
    insights.push(pullIns.length === 1
      ? { rule: 2, tone: 'info', title: d.pence !== null ? `One pull-in chance: +${fmtMoney(d.pence)}` : 'One pull-in chance',
          body: dealWords(d), link: { label: 'Review deal ›', dealId: d.dealId } }
      : { rule: 2, tone: 'info', title: `${pullIns.length} pull-in chances`,
          body: `${namesList(pullIns)} are Commit or Best case but dated after ${end}.` })
  }
  if (target === null) {
    insights.push({ rule: 3, tone: 'warning', title: 'No rep has a target set',
      body: `Set a target for each rep under Settings → Sales to see attainment for ${period} and for each rep.` })
  } else {
    const p = pctOf(attributed, target)
    if (p !== null && !ended && p < clock.elapsedPct - PACE_GAP_PTS) {
      insights.push({ rule: 4, tone: 'danger', title: 'Behind pace',
        body: `${p}% of rep targets won by linked reps with ${clock.elapsedPct}% of the ${kindWord} gone.` })
    }
    if (attributed >= target) {
      insights.push({ rule: 5, tone: 'success', title: 'Target hit',
        body: `${money(attributed / 100)} won by linked reps against ${money(target / 100)} in rep targets (+${money((attributed - target) / 100)}).` })
    }
  }
  const overdue = f.overdue
  if (overdue && overdue.count + overdue.excludedNonGbp > 0) {
    const n = overdue.count + overdue.excludedNonGbp
    const only = overdue.items.length === 1 ? overdue.items[0] : null
    insights.push({ rule: 6, tone: 'warning', title: `${n} ${plural(n, 'deal', 'deals')} past close date`,
      body: `${namesList(overdue.items)} ${plural(overdue.items.length, 'is', 'are')} still open with a close date before today.`,
      ...(only ? { link: { label: 'Update date ›', dealId: only.dealId } } : {}) })
  }

  // ── the rings ──
  let outerPct: number | null, outerLabel: string
  if (target !== null && target > 0) {
    outerPct = pctOf(attributed, target); outerLabel = 'Linked won of rep targets'
  } else {
    outerPct = pctOf(won.pence, headline.pence)
    outerLabel = scenario === 'stretch' ? 'Won of Stretch' : 'Won of Locked'
  }
  const elapsedLabel = `${f.periodKind === 'quarter' ? 'Quarter' : 'Month'} elapsed`
  const caption = clock.state === 'future'
    ? `Starts ${weekdayDayMonth(utc(f.periodStart))}`
    : ended ? `Ended ${weekdayDayMonth(clock.lastDayMs)}`
      : `Day ${clock.dayOfPeriod} of ${clock.daysInPeriod} · ends ${weekdayDayMonth(clock.lastDayMs)}`
  const rings = {
    outerPct, outerLabel, elapsedPct: clock.elapsedPct, elapsedLabel, caption,
    ariaLabel: `${outerLabel} ${outerPct === null ? 'not measurable' : `${outerPct}%`}; ${elapsedLabel.toLowerCase()} ${clock.elapsedPct}%`,
  }

  // ── the footer strip ──
  let footer: HeroModel['footer'] = null
  if (clock.state === 'current' && clock.daysLeft <= NEXT_UP_DAYS && f.nextPeriod) {
    const np = f.nextPeriod
    const nextName = periodName(np.periodKind, np.periodStart)
    const starts = weekdayDayMonth(utc(np.periodStart))
    if (np.count === 0) {
      footer = { kind: 'next', text: `Next up: ${nextName} starts ${starts} with no open deal dated inside it yet.` }
    } else {
      const peak = np.months.reduce((a, m) => (m.pence > a.pence ? m : a), np.months[0])
      const share = pctOf(peak.pence, np.pence)
      const monthName = MONTH_FULL[Number(peak.month.slice(5, 7)) - 1]
      footer = { kind: 'next', text: `Next up: ${nextName} starts ${starts} with ${fmtMoney(np.pence)} open across ${fencedDealCount(np.count, np.excludedNonGbp)}${share !== null ? `, ${share}% of it due in ${monthName}` : ''}.` }
    }
  } else if (clock.state === 'current') {
    const p = target !== null ? pctOf(attributed, target) : null
    footer = { kind: 'pace', text: p !== null
      ? `${p}% of rep targets won by linked reps with ${clock.elapsedPct}% of the ${kindWord} gone.`
      : `Won ${fmtMoney(won.pence)} with ${clock.elapsedPct}% of the ${kindWord} gone.` }
  }

  const asOf = f.asOf
  return {
    period, clock, scenario, showSwitch: !ended,
    asOfLabel: `As of ${asOf.slice(11, 16)} UTC, ${dayMonth(utc(asOf))}`,
    label: ended ? `${period} closed at` : `${period} will close at`,
    headline, headlineText: headlineMoney(headline.pence), headlineExact: money(headline.pence / 100),
    sentence, fenceNote: fenceNote(headline), won, bar,
    insights: insights.slice(0, 3), rings, footer,
  }
}
